import React, { Component } from 'react';
import moment from 'moment';
import EditSessionModal from './EditSessionModal';
import ForceCloseModal from './ForceCloseModal';

// Session status display configuration
const STATUS_CONFIG = {
  open: { label: 'Open', color: 'primary', icon: 'fa-clock-o' },
  closed: { label: 'Closed', color: 'success', icon: 'fa-check' },
  flagged: { label: 'Flagged', color: 'warning', icon: 'fa-flag' },
  auto_closed: { label: 'Auto-Closed', color: 'danger', icon: 'fa-exclamation-triangle' }
};

// Entry method display
const ENTRY_METHOD_CONFIG = {
  self_web: { label: 'Self', icon: 'fa-user' },
  self_mobile: { label: 'Self (Mobile)', icon: 'fa-mobile' },
  manager_web: { label: 'Manager', icon: 'fa-user-circle' },
  system: { label: 'System', icon: 'fa-cog' }
};

/**
 * SessionHistory - Display list of time tracking sessions
 *
 * Props:
 * - sessions: Array of session objects
 * - loading: Boolean - loading state
 * - technicianName: String - name for display in modals
 * - locationId: Number - location ID for API calls
 * - actorUserId: Number - the manager's user ID performing actions
 * - onSessionUpdate: Function - called after edit/close to refresh list
 */
class SessionHistory extends Component {
  state = {
    // Edit modal
    showEditModal: false,
    editingSession: null,

    // Force close modal
    showForceCloseModal: false,
    closingSession: null
  };

  // Open edit modal
  handleOpenEditModal = (session) => {
    this.setState({
      showEditModal: true,
      editingSession: session
    });
  };

  // Close edit modal
  handleCloseEditModal = () => {
    this.setState({
      showEditModal: false,
      editingSession: null
    });
  };

  // Open force close modal
  handleOpenForceCloseModal = (session) => {
    this.setState({
      showForceCloseModal: true,
      closingSession: session
    });
  };

  // Close force close modal
  handleCloseForceCloseModal = () => {
    this.setState({
      showForceCloseModal: false,
      closingSession: null
    });
  };

  // Handle successful edit
  handleEditSuccess = () => {
    this.handleCloseEditModal();
    if (this.props.onSessionUpdate) {
      this.props.onSessionUpdate();
    }
  };

  // Handle successful force close
  handleForceCloseSuccess = () => {
    this.handleCloseForceCloseModal();
    if (this.props.onSessionUpdate) {
      this.props.onSessionUpdate();
    }
  };

  // Format date header
  formatDateHeader = (dateStr) => {
    return moment(dateStr).format('ddd, MMM D');
  };

  // Format time
  formatTime = (timeStr) => {
    if (!timeStr) return '—';
    return moment(timeStr).format('h:mm A');
  };

  // Format duration - calculate from timestamps if seconds not provided
  formatDuration = (seconds, checkInAt, checkOutAt) => {
    let totalSeconds = seconds;

    // Calculate from timestamps if not provided
    if ((totalSeconds === null || totalSeconds === undefined) && checkInAt && checkOutAt) {
      totalSeconds = moment(checkOutAt).diff(moment(checkInAt), 'seconds');
    }

    if (!totalSeconds && totalSeconds !== 0) return 'In progress';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Get status badge
  getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'secondary', icon: 'fa-question' };
    return (
      <span className={`badge badge-${config.color}`}>
        <i className={`fa ${config.icon} mr-1`}></i>
        {config.label}
      </span>
    );
  };

  // Get entry method badge
  getEntryMethodBadge = (method) => {
    const config = ENTRY_METHOD_CONFIG[method] || { label: method, icon: 'fa-question' };
    return (
      <span className="badge badge-light" title={`Entry: ${config.label}`}>
        <i className={`fa ${config.icon}`}></i>
      </span>
    );
  };

  // Get border color for status
  getBorderColor = (status) => {
    switch (status) {
      case 'flagged':
        return '#ffc107'; // warning yellow
      case 'auto_closed':
        return '#dc3545'; // danger red
      case 'open':
        return '#007bff'; // primary blue
      default:
        return '#28a745'; // success green for closed
    }
  };

  render() {
    const { sessions, loading, technicianName } = this.props;
    const { showEditModal, editingSession, showForceCloseModal, closingSession } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading session history...</p>
        </div>
      );
    }

    // Empty state
    if (!sessions || sessions.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="fa fa-calendar-o fa-3x text-muted mb-3"></i>
          <h5>No Sessions Found</h5>
          <p className="text-muted">
            No time tracking sessions found for the selected date range.
          </p>
        </div>
      );
    }

    return (
      <div className="session-history">
        {/* Session List */}
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Date</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => {
                  const isOpen = session.status === 'open';

                  return (
                    <tr key={session.id} style={{ borderLeft: `4px solid ${this.getBorderColor(session.status)}` }}>
                      <td>
                        <strong>{this.formatDateHeader(session.check_in_at)}</strong>
                      </td>
                      <td>{this.formatTime(session.check_in_at)}</td>
                      <td className={!session.check_out_at ? 'text-muted' : ''}>
                        {this.formatTime(session.check_out_at)}
                      </td>
                      <td className={isOpen ? 'text-info' : ''}>
                        {this.formatDuration(session.total_seconds, session.check_in_at, session.check_out_at)}
                      </td>
                      <td>
                        {this.getStatusBadge(session.status)}
                        <span className="ml-2">
                          {this.getEntryMethodBadge(session.entry_method)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary mr-1"
                          onClick={() => this.handleOpenEditModal(session)}
                          title="Edit session"
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        {isOpen && (
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => this.handleOpenForceCloseModal(session)}
                            title="Force close session"
                          >
                            <i className="fa fa-stop"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Session Modal */}
        <EditSessionModal
          show={showEditModal}
          onHide={this.handleCloseEditModal}
          onSuccess={this.handleEditSuccess}
          session={editingSession}
          actorUserId={this.props.actorUserId}
        />

        {/* Force Close Modal */}
        <ForceCloseModal
          show={showForceCloseModal}
          onHide={this.handleCloseForceCloseModal}
          onSuccess={this.handleForceCloseSuccess}
          session={closingSession}
          technicianName={technicianName}
          actorUserId={this.props.actorUserId}
        />
      </div>
    );
  }
}

export default SessionHistory;
